/**
 * A single eased scalar — the "discrete beats" of §2.
 *
 * The spec hands these to GSAP, but the rendering model only ever asks that one
 * loop owns per-frame work and that the beats are plain eased scalars the loop
 * reads. Stepping them inside the existing rAF loop keeps the exact durations
 * and easings with no animation library in the critical path.
 */
export type Beat = {
    value: number
    from: number
    to: number
    elapsed: number
    delay: number
    duration: number
    ease: (t: number) => number
    running: boolean
    onComplete?: () => void
}

export const createBeat = (value = 0): Beat => ({
    value,
    from: value,
    to: value,
    elapsed: 0,
    delay: 0,
    duration: 0,
    ease: t => t,
    running: false
})

export const startBeat = (
    beat: Beat,
    to: number,
    duration: number,
    ease: (t: number) => number,
    delay = 0,
    onComplete?: () => void
): void => {
    beat.from = beat.value
    beat.to = to
    beat.elapsed = 0
    beat.delay = delay
    beat.duration = duration
    beat.ease = ease
    beat.running = true
    beat.onComplete = onComplete
}

/** Jump straight to the end, firing the completion callback (reduced motion). */
export const settleBeat = (beat: Beat, to: number): void => {
    beat.value = to
    beat.from = to
    beat.to = to
    beat.running = false

    const done = beat.onComplete
    beat.onComplete = undefined
    done?.()
}

export const stepBeat = (beat: Beat, dt: number): void => {
    if (!beat.running) return

    beat.elapsed += dt

    const t = beat.elapsed - beat.delay
    if (t < 0) return

    if (t >= beat.duration) {
        beat.value = beat.to
        beat.running = false

        const done = beat.onComplete
        beat.onComplete = undefined
        done?.()

        return
    }

    beat.value = beat.from + (beat.to - beat.from) * beat.ease(t / beat.duration)
}
